import { cn } from "@vc/lib/utils"
import { Wifi, WifiOff, AlertTriangle, Signal } from "lucide-react"

/** "unknown" is a real state: nothing measured yet. It is not a failure, and
 *  it must never be rendered as a plausible number — that is what this replaced. */
export type NetworkQuality = "excellent" | "good" | "fair" | "poor" | "offline" | "unknown"

interface NetworkQualityIndicatorProps {
  quality: NetworkQuality
  bitrate?: number | null
  packetLoss?: number | null
  latency?: number | null
  showDetails?: boolean
  size?: "sm" | "md" | "lg"
}

export function NetworkQualityIndicator({
  quality,
  bitrate,
  packetLoss,
  latency,
  showDetails = false,
  size = "sm"
}: NetworkQualityIndicatorProps) {
  const getQualityConfig = (quality: NetworkQuality) => {
    switch (quality) {
      case "excellent":
        return {
          icon: <Signal className="h-3 w-3" />,
          color: "text-green-500",
          bg: "bg-green-500/10",
          border: "border-green-500/30",
          label: "Excellent",
          description: "Very good connection"
        }
      case "good":
        return {
          icon: <Wifi className="h-3 w-3" />,
          color: "text-blue-500",
          bg: "bg-blue-500/10",
          border: "border-blue-500/30",
          label: "Good",
          description: "Good connection"
        }
      case "fair":
        return {
          icon: <Wifi className="h-3 w-3" />,
          color: "text-yellow-500",
          bg: "bg-yellow-500/10",
          border: "border-yellow-500/30",
          label: "Fair",
          description: "Connection could be better"
        }
      case "poor":
        return {
          icon: <AlertTriangle className="h-3 w-3" />,
          color: "text-orange-500",
          bg: "bg-orange-500/10",
          border: "border-orange-500/30",
          label: "Poor",
          description: "Poor connection"
        }
      case "unknown":
        return {
          icon: <Wifi className="h-3 w-3" />,
          color: "text-gray-400 dark:text-foreground/40",
          bg: "bg-gray-500/10",
          border: "border-gray-500/20",
          label: "Measuring",
          description: "No measurement yet"
        }
      case "offline":
        return {
          icon: <WifiOff className="h-3 w-3" />,
          color: "text-red-500",
          bg: "bg-red-500/10",
          border: "border-red-500/30",
          label: "Offline",
          description: "No connection"
        }
      default:
        return {
          icon: <WifiOff className="h-3 w-3" />,
          color: "text-gray-900 dark:text-foreground/80",
          bg: "bg-gray-500/10",
          border: "border-gray-500/30",
          label: "Unknown",
          description: "Connection status unknown"
        }
    }
  }

  const config = getQualityConfig(quality)
  
  const sizeClasses = {
    sm: "w-5 h-5 text-xs",
    md: "w-6 h-6 text-sm",
    lg: "w-7 h-7 text-base"
  }

  if (showDetails) {
    return (
      <div className="flex items-center gap-2 text-xs text-white/70">
        <div className={cn(
          "flex items-center justify-center rounded-full border",
          config.bg,
          config.border,
          sizeClasses[size]
        )}>
          <div className={config.color}>
            {config.icon}
          </div>
        </div>
        <div>
          <div className="font-medium text-white/90">{config.label}</div>
          {bitrate && (
            <div className="text-white/50">
              {bitrate} kbps
              {packetLoss && ` • ${packetLoss}% loss`}
              {latency && ` • ${latency}ms`}
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={cn(
      "flex items-center justify-center rounded-full border transition-all duration-200",
      config.bg,
      config.border,
      sizeClasses[size]
    )}>
      <div className={config.color}>
        {config.icon}
      </div>
    </div>
  )
}
